import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Edit, Trash2 } from 'lucide-react';
import Modal from '../common/Modal';
import apiService from '../../services/api';

const UniversityCountries = () => {
  const [universityCountries, setUniversityCountries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    flag: '',
    description: '',
    status: 'active',
    country_id: ''
  });

  useEffect(() => {
    fetchUniversityCountries();
    fetchCountries();
  }, [pagination.page, searchTerm, statusFilter]);

  const fetchUniversityCountries = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      };

      const response = await apiService.getUniversityCountries(params);
      setUniversityCountries(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (err) {
      setError('Failed to fetch university countries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.getCountries({ limit: 100 });
      setCountries(response.data);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await apiService.updateUniversityCountry(editingItem.id, formData);
      } else {
        await apiService.createUniversityCountry(formData);
      }

      setShowModal(false);
      setEditingItem(null);
      resetForm();
      fetchUniversityCountries();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      code: item.code,
      flag: item.flag || '',
      description: item.description || '',
      status: item.status,
      country_id: item.country_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this university country?')) {
      try {
        await apiService.deleteUniversityCountry(id);
        fetchUniversityCountries();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      flag: '',
      description: '',
      status: 'active',
      country_id: ''
    });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
    resetForm();
  };

  const handleView = (item) => {
    setViewingItem(item);
    setShowViewModal(true);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewingItem(null);
  };

  const handleAddUniversityCountry = () => {
    setEditingItem(null);
    resetForm();
    setShowModal(true);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (loading) return <div className="loading">Loading university countries...</div>;

  return (
    <div className="university-countries-management">
      <div className="header">
        <h2>University Countries Management</h2>
        <button
          className="btn btn-primary"
          onClick={handleAddUniversityCountry}
        >
          Add University Country
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="filters">
        <input
          type="text"
          placeholder="Search university countries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-control"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Flag</th>
              <th>Country</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {universityCountries.map(item => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.code}</td>
                <td>{item.flag}</td>
                <td>{item.country?.name || 'N/A'}</td>
                <td>
                  <span className={`badge ${item.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>
                    {item.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <motion.button
                      className="btn-sm info touch-target"
                      onClick={() => handleView(item)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="View"
                    >
                      <Eye size={12} />
                    </motion.button>
                    <motion.button
                      className="btn-sm primary touch-target"
                      onClick={() => handleEdit(item)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Edit"
                    >
                      <Edit size={12} />
                    </motion.button>
                    <motion.button
                      className="btn-sm danger touch-target"
                      onClick={() => handleDelete(item.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </motion.button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          disabled={pagination.page === 1}
          onClick={() => handlePageChange(pagination.page - 1)}
        >
          Previous
        </button>
        <span>Page {pagination.page} of {pagination.totalPages}</span>
        <button
          disabled={pagination.page === pagination.totalPages}
          onClick={() => handlePageChange(pagination.page + 1)}
        >
          Next
        </button>
      </div>

      <Modal
        isOpen={showModal}
        onClose={handleModalClose}
        title={`${editingItem ? 'Edit' : 'Add'} University Country`}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Code *</label>
            <input
              type="text"
              required
              maxLength="3"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Flag</label>
            <input
              type="text"
              value={formData.flag}
              onChange={(e) => setFormData({ ...formData, flag: e.target.value })}
              className="form-control"
              placeholder="Flag emoji or URL"
            />
          </div>
          <div className="form-group">
            <label>Country</label>
            <select
              value={formData.country_id}
              onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
              className="form-control"
            >
              <option value="">Select Country</option>
              {countries.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="form-control"
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="form-control"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingItem ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showViewModal}
        onClose={handleViewModalClose}
        title="University Country Details"
      >
        {viewingItem && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                {'🏛️'}
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{viewingItem.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>University Country</p>
            </div>

            {/* Details Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Country Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px' }}>Code:</span>
                  <span style={{ color: '#374151' }}>{viewingItem.code}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px' }}>Flag:</span>
                  <span style={{ color: '#374151' }}>{viewingItem.flag || 'N/A'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontWeight: '500', color: '#6b7280', minWidth: '60px' }}>Country:</span>
                  <span style={{ color: '#374151' }}>{viewingItem.country?.name || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Description Section */}
            {viewingItem.description && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Description</h4>
                <div style={{
                  padding: '1rem',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  color: '#374151',
                  lineHeight: '1.6'
                }}>
                  {viewingItem.description}
                </div>
              </div>
            )}

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: viewingItem.status === 'active' ? '#dcfce7' : '#f3f4f6',
                color: viewingItem.status === 'active' ? '#166534' : '#6b7280',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: viewingItem.status === 'active' ? '#22c55e' : '#9ca3af',
                  borderRadius: '50%'
                }} />
                {viewingItem.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UniversityCountries;